import React from "react";
import { Button, Divider, Typography, Box } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { useIntl } from "react-intl";

/**
 * Reusable navigation component for multi-step forms
 */
const FormNavigation = ({
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onSubmit,
  isSubmitting,
  canProceed = true,
  validationError = null,
}: {
  currentStep: number;
  totalSteps: number;
  onNext: (ev?: React.MouseEvent) => void;
  onPrevious: (ev?: React.MouseEvent) => void;
  onSubmit?: (ev?: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  canProceed?: boolean;
  validationError?: string | null;
}) => {
  const { formatMessage } = useIntl();
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  return (
    <Box>
      {currentStep > 0 && <Divider sx={{ my: 2 }} />}

      {/* Validation Error Display */}
      {validationError && (
        <>
          <Button onClick={onPrevious} fullWidth>
            <Typography
              variant="body1"
              color="error"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ErrorOutline /> {validationError}
            </Typography>
          </Button>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Navigation Buttons */}
      {isLastStep ? (
        <>
          <Button
            size="large"
            sx={{ mb: 1 }}
            variant="outlined"
            fullWidth
            onClick={onPrevious}
          >
            {formatMessage({ id: "ticket.create.form.button.label.back" })}
          </Button>
          <Button
            size="large"
            fullWidth
            variant="contained"
            color="secondary"
            type="submit"
            disabled={isSubmitting || !canProceed}
            title={
              !canProceed ? "Please fill out all required fields" : undefined
            }
          >
            {formatMessage({ id: "ticket.create.form.button.label.submit" })}
          </Button>
        </>
      ) : (
        <>
          {!isFirstStep && (
            <Button
              size="large"
              fullWidth
              variant="outlined"
              sx={{ mb: 1 }}
              onClick={onPrevious}
            >
              {formatMessage({ id: "ticket.create.form.button.label.back" })}
            </Button>
          )}
          {!isFirstStep && (
            <Button
              size="large"
              variant="contained"
              color="secondary"
              fullWidth
              onClick={onNext}
            >
              {formatMessage({ id: "ticket.create.form.button.label.next" })}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default FormNavigation;
